import { jwtDecode } from 'jwt-decode'

import { appConfig } from '../../config'
import cookieStorage from '../cookie'

const ACCESS_TOKEN_KEY = 'accessToken'
const LEGACY_JWT_COOKIE = 'jwt'
const MERCHANT_COUNTRY_COOKIE = 'currentMerchantCountryCode'
const MERCHANT_ID_COOKIE = 'currentMerchantID'
const REFRESH_TOKEN_COOKIE = 'refreshToken'

const listeners = new Set()
let inFlightRefreshPromise = null

const emitAuthChange = () => {
	listeners.forEach(listener => listener())
}

const getTokenExpiryDate = token => new Date(jwtDecode(token).exp * 1000)

const isTokenExpired = token => {
	try {
		return jwtDecode(token).exp < Date.now() / 1000
	} catch {
		return true
	}
}

const storeAccessToken = accessToken => {
	localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
	emitAuthChange()
}

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY) ?? undefined

export const clearAccessToken = () => {
	localStorage.removeItem(ACCESS_TOKEN_KEY)
	emitAuthChange()
}

export const getRefreshToken = () => cookieStorage.getItem(REFRESH_TOKEN_COOKIE)

export const subscribeToAuthChanges = listener => {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

export const storeLoginTokens = ({ accessToken, refreshToken }) => {
	localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)

	if (refreshToken) {
		cookieStorage.setItem(REFRESH_TOKEN_COOKIE, refreshToken, getTokenExpiryDate(refreshToken))
	} else {
		cookieStorage.removeItem(REFRESH_TOKEN_COOKIE)
	}

	cookieStorage.removeItem(MERCHANT_ID_COOKIE)
	cookieStorage.removeItem(MERCHANT_COUNTRY_COOKIE)
	emitAuthChange()
}

export const clearAuthState = () => {
	localStorage.removeItem(ACCESS_TOKEN_KEY)
	cookieStorage.removeItem(LEGACY_JWT_COOKIE)
	cookieStorage.removeItem(ACCESS_TOKEN_KEY)
	cookieStorage.removeItem(REFRESH_TOKEN_COOKIE)
	cookieStorage.removeItem(MERCHANT_ID_COOKIE)
	cookieStorage.removeItem(MERCHANT_COUNTRY_COOKIE)
	emitAuthChange()
}

const refreshAccessToken = async () => {
	if (inFlightRefreshPromise) {
		return inFlightRefreshPromise
	}

	const refreshToken = getRefreshToken()

	if (!refreshToken) {
		clearAuthState()
		return null
	}

	inFlightRefreshPromise = (async () => {
		try {
			const response = await fetch(`${appConfig.identityService}/refresh`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					authorization: `Bearer ${refreshToken}`,
				},
			})

			if (!response.ok) {
				throw new Error(`Token refresh failed with status ${response.status}`)
			}

			const jsonData = await response.json()
			const nextAccessToken = jsonData.access_token

			if (!nextAccessToken) {
				throw new Error('Token refresh failed without access token')
			}

			storeAccessToken(nextAccessToken)
			return nextAccessToken
		} catch (error) {
			console.log('error', error)
			clearAuthState()
			return null
		} finally {
			inFlightRefreshPromise = null
		}
	})()

	return inFlightRefreshPromise
}

export const getValidAccessToken = async () => {
	const accessToken = getAccessToken()

	if (accessToken && !isTokenExpired(accessToken)) {
		return accessToken
	}

	return refreshAccessToken()
}
